import { createAdminApiClient } from '../adminApiClient';

/**
 * @typedef {{ current_page: number, last_page: number, total: number, per_page: number }} PaginationMeta
 */

/**
 * Service para el detalle de usuarios en admin.
 * Reemplaza los Vuex actions de AdminUsuariosModule.js
 *
 * IMPORTANTE: los parámetros se pasan PLANOS (no envueltos en filters)
 * porque el legacy api.call() aplana el objeto filters como query params directos.
 * createAdminApiClient ya inyecta api_key via interceptor.
 */
export default {
    /**
     * Obtiene los datos de un usuario.
     * GET /usuarios/{user_id}?lista_id=xxx&per_page=xxx
     *
     * @param {{ lista_id: number|string, user_id: number|string }} params
     * @returns {Promise<{ user: Object }>}
     */
    async getPerfil({ lista_id, user_id }) {
        const client = createAdminApiClient();

        const response = await client.get(`/usuarios/${user_id}`, {
            params: {
                lista_id,
                per_page: 999,
            },
        });

        return response.data ?? {};
    },

    /**
     * Obtiene los abonos de un usuario.
     * GET /usuarios/{user_id}/abonos?lista_id=xxx&per_page=xxx
     *
     * @param {{ lista_id: number|string, user_id: number|string, page?: number, per_page?: number }} params
     * @returns {Promise<{ items: Array, meta: PaginationMeta }>}
     */
    async getAbonos({ lista_id, user_id, page = 1, per_page = 10 }) {
        const client = createAdminApiClient();

        const response = await client.get(`/usuarios/${user_id}/abonos`, {
            params: {
                lista_id,
                per_page,
            },
        });

        const data = response.data ?? {};
        const rawItems = data.data ?? data.abonos ?? [];
        const items = Array.isArray(rawItems) ? rawItems : [];
        const meta = {
            current_page: data.current_page ?? data.meta?.current_page ?? page,
            last_page: data.last_page ?? data.meta?.last_page ?? 1,
            total: data.total ?? data.meta?.total ?? items.length,
            per_page: data.per_page ?? data.meta?.per_page ?? per_page,
        };

        return { items, meta };
    },

    /**
     * Obtiene los deportistas (bases) de un usuario.
     * GET /usuarios/{user_id}/bases?lista_id=xxx&per_page=xxx
     *
     * @param {{ lista_id: number|string, user_id: number|string, page?: number, per_page?: number }} params
     * @returns {Promise<{ items: Array, meta: PaginationMeta }>}
     */
    async getBases({ lista_id, user_id, page = 1, per_page = 10 }) {
        const client = createAdminApiClient();

        const response = await client.get(`/usuarios/${user_id}/bases`, {
            params: {
                lista_id,
                per_page,
            },
        });

        const data = response.data ?? {};
        const rawItems = data.data ?? data.bases ?? [];
        const items = Array.isArray(rawItems) ? rawItems : [];
        const meta = {
            current_page: data.current_page ?? data.meta?.current_page ?? page,
            last_page: data.last_page ?? data.meta?.last_page ?? 1,
            total: data.total ?? data.meta?.total ?? items.length,
            per_page: data.per_page ?? data.meta?.per_page ?? per_page,
        };

        return { items, meta };
    },

    /**
     * Obtiene la información de pago de un usuario.
     * GET /usuarios/{userId}/info_pago?tid=xxx
     *
     * @param {{ userId: number|string, tid: string, lista_id: number|string }} params
     * @returns {Promise<Object>} con { titular, panVuePayCard, mesCaducidad, anoCaducidad } o null
     */
    async getInfoPago({ userId, tid, lista_id }) {
        const client = createAdminApiClient();

        const response = await client.get(`/usuarios/${userId}/info_pago`, {
            params: { tid, lista_id },
        });

        return response.data ?? null;
    },

    /**
     * Obtiene todas las parcelas vendidas de una lista.
     * GET /listas-abonados/{lista_id}/parcelas/parcelas-vendidas
     *
     * @param {{ lista_id: number|string }} params
     * @returns {Promise<Array>}
     */
    async getParcelas({ lista_id }) {
        const client = createAdminApiClient();

        const response = await client.get(`/listas-abonados/${lista_id}/parcelas/parcelas-vendidas`);

        const data = response.data ?? {};
        const rawItems = data.data ?? data.parcelas ?? [];

        return Array.isArray(rawItems) ? rawItems : [];
    },
};
